import React, { useEffect, useRef } from 'react';

const OtpModal: React.FC<{
  email: string;
  otp: string[];
  timer: number;
  resendDisabled: boolean;
  isOtpValid: boolean;
  onOtpChange: (index: number, value: string) => void;
  onOtpKeyDown: (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => void;
  onResend: () => void;
  onContinue: () => void;
  onClose: () => void;
  isForgotPassword?: boolean;
}> = ({
  email,
  otp,
  timer,
  resendDisabled,
  isOtpValid,
  onOtpChange,
  onOtpKeyDown,
  onResend,
  onContinue,
  onClose,
  isForgotPassword = false,
}) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    useEffect(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, []);

    const handleOtpChange = (index: number, value: string) => {
      if (value.length > 1) return;
      if (value && !/^\d$/.test(value)) return;

      onOtpChange(index, value);

      if (value && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace') {
        e.preventDefault();

        if (otp[index]) {
          onOtpChange(index, '');
        } else if (index > 0) {
          const prevIndex = index - 1;
          onOtpChange(prevIndex, '');
          inputRefs.current[prevIndex]?.focus();
        }
      }

      if (e.key === 'ArrowLeft' && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }

      if (e.key === 'ArrowRight' && index < otp.length - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      onOtpKeyDown(index, e);
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text/plain').trim();

      if (/^\d{6}$/.test(pastedData)) {
        const digits = pastedData.split('');
        digits.forEach((digit, index) => {
          if (index < otp.length) {
            onOtpChange(index, digit);
          }
        });
        const lastFilledIndex = Math.min(digits.length, otp.length) - 1;
        inputRefs.current[lastFilledIndex]?.focus();
      }
    };

    const formatTimer = (seconds: number) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    return (
      <div className='fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50'>
        <div
          className='bg-white rounded-2xl shadow-lg max-w-md w-full text-center'
          style={{
            padding: '20px',
          }}
        >
          <h3
            className='text-xl font-semibold mb-1 text-left'
            style={{
              color: '#3F5265',
              fontSize: '16px',
            }}
          >
            {isForgotPassword ? 'Forget password?' : 'Verify your email'}
          </h3>
          <p
            className='text-gray-600 text-sm mb-6 text-left'
            style={{
              color: '#657584',
              fontSize: '12px',
            }}
          >
            {isForgotPassword ? (
              <>
                We've send the one time password (OTP) on{' '}
                <span className='text-blue-500'>{email}</span>
              </>
            ) : (
              <>
                We&apos;ve send the verification code on{' '}
                <span className='text-blue-500'>{email}</span>
              </>
            )}
          </p>

          <div
            style={{
              paddingLeft: '30px',
              paddingRight: '30px',
              paddingTop: '30px',
              paddingBottom: '0px',
            }}
          >
            <div className='flex flex-wrap justify-center gap-1 mb-2 max-w-full' onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el }}
                  id={`otp-input-${index}`}
                  type='text'
                  inputMode='numeric'
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className='sm:w-14 sm:h-14 text-center border border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-orange-400'
                  maxLength={1}
                  style={{
                    width: '50px',
                    height: '50px',
                  }}
                  autoComplete='off'
                  autoCorrect='off'
                  spellCheck='false'
                />
              ))}
            </div>
          </div>

          <p
            className='text-sm text-gray-500 mb-16'
            style={{
              color: '#3F5265',
              fontSize: '12px',
              fontWeight: '500',
            }}
          >
            Re-send code{' '}
            {timer > 0 ? (
              <>
                in <span className='text-blue-500'>{formatTimer(timer)}</span>
              </>
            ) : (
              'now'
            )}
          </p>

          <div className='flex gap-3 justify-end'>
            <button
              onClick={onResend}
              disabled={resendDisabled}
              className={`px-3 py-1 border rounded-sm text-sm font-medium ${resendDisabled
                ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                : 'border-orange-400 text-orange-500 hover:bg-orange-50'
                }`}
              style={{
                fontSize: '12px',
              }}
            >
              Re-send
            </button>
            <button
              onClick={onContinue}
              disabled={!isOtpValid}
              className={`px-3 py-1 rounded-sm text-sm font-medium text-white ${isOtpValid
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-gray-300 cursor-not-allowed'
                }`}
              style={{
                fontSize: '12px',
              }}
            >
              {isForgotPassword ? 'Continue' : 'Confirm'}
            </button>
          </div>
        </div>
      </div>
    );
  };

export default OtpModal;